import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

const STORAGE_KEY = 'silabvet-sidebar-collapsed';

/**
 * Debe valer lo mismo que el breakpoint `lg` de Tailwind y que
 * `--breakpoint-escritorio` en tailwind.css: de ahí para arriba el menú es una
 * columna del armazón, y de ahí para abajo un cajón que se superpone.
 */
const ESCRITORIO = '(min-width: 64rem)';

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private readonly router = inject(Router);

  /** Menú reducido a rail de iconos. Solo aplica en escritorio. */
  readonly sidebarCollapsed = signal<boolean>(
    localStorage.getItem(STORAGE_KEY) === 'true',
  );

  /** Cierto a partir de `lg`; abajo el menú no ocupa lugar en el flujo. */
  readonly esEscritorio = signal(true);

  /** Cajón del menú abierto en pantallas angostas. */
  readonly sidebarMovilAbierto = signal(false);

  /**
   * Si el menú debe dibujarse como rail de iconos. En el cajón móvil siempre va
   * completo, aunque en escritorio haya quedado colapsado.
   */
  readonly sidebarCompacto = computed(
    () => this.esEscritorio() && this.sidebarCollapsed(),
  );

  /** Modal de perfil del usuario (lo abren la topbar y la tuerca del sidebar). */
  readonly perfilAbierto = signal(false);

  constructor() {
    // jsdom (tests) no implementa matchMedia; ahí se asume escritorio.
    if (typeof window.matchMedia === 'function') {
      const media = window.matchMedia(ESCRITORIO);
      this.esEscritorio.set(media.matches);
      media.addEventListener('change', (evento) => {
        this.esEscritorio.set(evento.matches);
        if (evento.matches) {
          this.sidebarMovilAbierto.set(false);
        }
      });
    }

    // Navegar desde el cajón lo deja abierto tapando la pantalla nueva.
    this.router.events.subscribe((evento) => {
      if (evento instanceof NavigationEnd) {
        this.sidebarMovilAbierto.set(false);
      }
    });

    effect(() => {
      localStorage.setItem(STORAGE_KEY, String(this.sidebarCollapsed()));
    });
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((collapsed) => !collapsed);
  }

  abrirSidebarMovil(): void {
    this.sidebarMovilAbierto.set(true);
  }

  cerrarSidebarMovil(): void {
    this.sidebarMovilAbierto.set(false);
  }
}
